import qs from 'qs';
import i18next from 'i18next';
import { showToast } from 'stores/ToastStore';
import request from 'superagent';
import fileDownload from 'js-file-download';
import moment from 'moment';

import { getBearerGrantTypeAuthHeaders } from '../helpers/auth_helpers';
import client from './rest/AdminClient';

const ERRORS = {
  APPOINTMENT_AFTER_LFD: 'APPOINTMENT_APPROVAL_ERROR',
  SHIPMENT_ON_HOLD: 'APPOINTMENT_APPROVAL_HOLD_ERROR',
  NOT_FOUND: 'NO_SHIPMENT_TRY_AGAIN',
  YARD_LAST_FREE_DATE_REQUIRED_TO_CALCULATE_FEES_DUE:
    'YARD_LFD_REQUIRED_TO_CALCULATE_DUE',
};

const ONE_WEEK = 7;

export async function updateAppointment(
  appointmentId,
  appointment,
  options = { showAlert: true },
) {
  const showAlert = !!options?.showAlert;
  try {
    const result = await client({
      method: 'PATCH',
      path: `/api/appointments/${appointmentId}`,
      entity: JSON.stringify(appointment),
    });
    if (showAlert) {
      showToast(i18next.t('APPOINTMENT_UPDATED_SUCCESS'), null, 'success');
    }
    return result.entity.result.items;
  } catch (e) {
    if (showAlert) {
      showToast(
        i18next.t('APPOINTMENT_UPDATE_FAILED'),
        i18next.t('APPOINTMENT_UPDATE_FAILED_WITH_ERROR', {
          error: e.entity.error,
        }),
        'error',
      );
    }
    throw e;
  }
}

export async function markCommentsAsRead(appointmentId) {
  try {
    const result = await client({
      method: 'POST',
      path: `/api/appointments/${appointmentId}/updates/acknowledge`,
    });
    return result.entity?.result?.items?.[0];
  } catch (e) {
    showToast(
      i18next.t('APPOINTMENT_UPDATE_FAILED'),
      i18next.t('APPOINTMENT_UPDATE_FAILED_WITH_ERROR', {
        error: e.entity.error,
      }),
      'error',
    );
    throw e;
  }
}

export async function validateCarrierAppointmentDetails(data) {
  try {
    const result = await client({
      method: 'POST',
      path: '/api/appointments/carrier/validate',
      entity: JSON.stringify(data),
    });
    return result.entity;
  } catch (e) {
    const err = e.entity.error;
    const message = ERRORS[err] ? i18next.t(ERRORS[err]) : err;

    showToast(
      i18next.t('FAILED_TO_FIND_APPOINTMENT'),
      i18next.t('FAILED_TO_VALIDATE_APPOINTMENT_WITH_MSG', { message }),
      'error',
    );
    throw e;
  }
}

export async function createAppointment(appointment) {
  try {
    const result = await client({
      method: 'POST',
      path: `/api/yards/${appointment.yard}/appointments`,
      entity: JSON.stringify(appointment),
    });
    showToast(
      i18next.t('THANK_YOU_MSG_FOR_SCHEDULE_APPOINTMENT'),
      null,
      'success',
    );
    return result?.entity?.result;
  } catch (e) {
    showToast(
      i18next.t('FAILED_TO_CREATE_APPOINTMENT'),
      i18next.t('FAILED_TO_CREATE_APPOINTMENT_WITH_ERROR', {
        error: e.entity.error,
      }),
      'error',
    );
    throw e;
  }
}

export async function searchAppointments(query = {}) {
  const queryString = qs.stringify(query);
  const result = await client({
    method: 'GET',
    path: `/api/appointments/selections?${queryString}`,
  });

  if (result.status.code !== 200) {
    showToast(
      i18next.t('FAILED_TO_GET_APPOINTMENTS'),
      i18next.t('FAILED_TO_GET_APPOINTMENTS_WITH_ERROR', {
        error: result.entity.error,
      }),
      'error',
    );
  } else {
    return result.entity.result;
  }
}

export async function acknowledgeAppointmentUpdate(id) {
  const result = await client({
    method: 'POST',
    path: `/api/appointments/${id}/updates/acknowledge`,
  });

  if (result.status.code !== 200) {
    showToast(
      i18next.t('FAILED_TO_ACK_APPOINTMENT_UPADTE'),
      i18next.t('FAILED_TO_ACK_APPOINTMENT_UPADTE_WITH_ERROR', {
        error: result.entity.error,
      }),
      'error',
    );
  } else {
    return result.entity.result;
  }
}
export async function getAppointments(query = {}) {
  const queryString = qs.stringify(query);
  const result = await client({
    method: 'GET',
    path: `/api/appointments?${queryString}`,
  });

  if (result.status.code !== 200) {
    showToast(
      i18next.t('FAILED_TO_GET_APPOINTMENTS'),
      i18next.t('FAILED_TO_GET_APPOINTMENTS_WITH_ERROR', {
        error: result.entity.error,
      }),
      'error',
    );
  } else {
    return result.entity.result;
  }
}

export async function getAppointment(id) {
  const { items: appointments } = await getAppointments({ _id: id });

  const appointment = appointments[0];

  if (!appointment) {
    showToast(
      i18next.t('FAILED_TO_GET_APPOINTMENT'),
      i18next.t('FAILED_TO_GET_APPOINTMENT'),
      'error',
    );
  }
  return appointment;
}

export async function getAppointmentStatusMetrics(fromDate, toDate) {
  const queryString = qs.stringify({ fromDate, toDate });
  const result = await client({
    method: 'GET',
    path: `/api/appointments/status?${queryString}`,
  });

  if (result.status.code !== 200) {
    showToast(
      i18next.t('FAILED_TO_GET_METRICS'),
      i18next.t('FAILED_TO_GET_METRICS_WITH_ERROR', {
        error: result.entity.error,
      }),
      'error',
    );
  } else {
    return result.entity.result;
  }
}

export async function cancelAppointment(id) {
  const result = await client({
    method: 'PATCH',
    path: `/api/appointments/${id}/cancel`,
  });

  if (result.status.code !== 200) {
    showToast(
      i18next.t('FAILED_TO_CANCEL_APPOINTMENT'),
      i18next.t('FAILED_TO_CANCEL_APPOINTMENT_WITH_ERROR', {
        error: result.entity.error,
      }),
      'error',
    );
  } else {
    showToast(i18next.t('APPOINTMENT_CANCEL_SUCCESS'), null, 'success');
    return result.entity.result;
  }
}

export async function getAppointmentFees({
  master_bill_number,
  container_number,
  window_start,
  window_end,
  yard,
}) {
  try {
    const result = await client({
      method: 'POST',
      path: '/api/appointments/fees',
      entity: JSON.stringify({
        master_bill_number,
        container_number,
        window_start,
        window_end,
        yard,
      }),
    });
    return result?.entity?.data;
  } catch (result) {
    const { error } = result.entity;
    const message = i18next.t(ERRORS[error]) || i18next.t(error);
    console.log('avamad', message);
    showToast(
      i18next.t('FAILED_TO_GET_OUTSTANDING_FEES'),
      i18next.t('FAILED_TO_GET_OUTSTANDING_FEES_WITH_MSG', { message }),
      'error',
    );
    throw result;
  }
}

export async function updateAppointmentStatus(id, status) {
  try {
    const result = await client({
      method: 'PATCH',
      path: `/admin/api/appointments/${id}/status`,
      entity: JSON.stringify({
        status,
      }),
    });
    showToast(i18next.t('APPOINTMENT_STATUS_UPDATED'), null, 'success');
    return result.entity.result;
  } catch (result) {
    const { error } = result.entity;
    const message = i18next.t(ERRORS[error]) || error;
    showToast(
      i18next.t('APPOINTMENT_STATUS_UPDATE_FAILED'),
      i18next.t('APPOINTMENT_STATUS_UPDATE_FAILED_WITH_MSG', { message }),
      'error',
    );
    throw result;
  }
}

export async function getAppointmentLocations() {
  const result = await client({
    method: 'GET',
    path: '/api/yards',
    entity: JSON.stringify({
      select: '_id name',
    }),
  });

  if (result.status.code !== 200) {
    showToast(
      i18next.t('FAILED_TO_GET_APPOINTMENT_LOCATION'),
      i18next.t('FAILED_TO_GET_APPOINTMENT_LOCATION_WITH_ERROR', {
        error: result.entity.error,
      }),
      'error',
    );
  } else {
    return result.entity.result;
  }
}
function formatDate(dateObject, hours, minutes) {
  const hourString = `${hours}`.padStart(2, '0');
  const minuteString = `${minutes}`.padStart(2, '0');
  const date = `${dateObject.format(
    'YYYY-MM-DD',
  )} ${hourString}:${minuteString}`;
  return date;
}

export async function getAppointmentSlots({
  yardId,
  timezone,
  weekStart,
  weekEnd,
}) {
  try {
    const queryString = qs.stringify({
      weekStart,
      weekEnd,
    });

    const res = await client({
      method: 'GET',
      path: `/api/yards/${yardId}/appointments/available?${queryString}`,
    });

    const items = res?.entity.result;

    const weekStartObj = moment(weekStart);

    const now = moment();
    const appointmentSlots = [...Array(ONE_WEEK).keys()].map((i) => {
      const date = weekStartObj.clone().add(i, 'days');
      const offset = date.day();

      const slots = items[offset].slots.map((appointmentSlot) => {
        const { startHour, startMinute, endHour, endMinute } = appointmentSlot;
        const windowStart = moment.tz(
          formatDate(date, startHour, startMinute),
          timezone,
        );
        const windowEnd = moment.tz(
          formatDate(date, endHour, endMinute),
          timezone,
        );

        const label = windowStart.format('hh:mm A');

        const disabled = now.isAfter(windowStart);
        return {
          ...appointmentSlot,
          offset,
          startHour,
          startMinute,
          endHour,
          endMinute,
          windowStart,
          windowEnd,
          disabled,
          label,
        };
      });

      return {
        date: date.toDate(),
        label: date.format('ddd, MMM D'),
        offset,
        slots,
      };
    });

    return appointmentSlots;
  } catch (e) {
    console.log(e);
    showToast(
      i18next.t('FAILED_TO_GET_SLOTS'),
      i18next.t('FAILED_TO_GET_SLOTS_WITH_ERROR', { error: e.entity.error }),
      'error',
    );
    throw e;
  }
}

export async function getYardByContainer({ consignee, containerNumber }) {
  try {
    const res = await client({
      method: 'GET',
      path: `/api/yards/container/${containerNumber}/consignee/${consignee}`,
    });

    return res.entity.result;
  } catch (e) {
    showToast(
      i18next.t('FAILED_TO_FIND_YARD'),
      i18next.t('FAILED_TO_FIND_YARD'),
      'error',
    );
    throw e;
  }
}

export async function addComment(appointmentId, message) {
  try {
    const res = await client({
      method: 'POST',
      path: `/api/appointments/${appointmentId}/comment`,
      entity: JSON.stringify({
        message,
      }),
    });
    showToast(i18next.t('COMMENT_ADDED'), null, 'success');
    return res.entity?.result?.items?.[0]?.comments || [];
  } catch (e) {
    showToast(
      i18next.t('FAILED_TO_CREATE_APPOINTMENT_COMMENT'),
      i18next.t('FAILED_TO_CREATE_APPOINTMENT_COMMENT_WITH_ERROR', {
        error: e.entity.error,
      }),
      'error',
    );
    throw e;
  }
}

export async function updateContainerAvailabilityBulk(
  file,
  suppressEmail = false,
) {
  try {
    return new Promise((resolve, reject) => {
      const req = request.post(
        `${API_HOST}/admin/api/appointments/container-available/bulk`,
      );
      req.set('Authorization', getBearerGrantTypeAuthHeaders());
      req.field('suppressEmail', JSON.stringify(suppressEmail));
      req.attach('file', file);

      req.end((err, response) => {
        if (err) {
          if (response.type === 'application/json') {
            showToast(
              i18next.t('BULK_CONTAINER_AVAILABILITY'),
              i18next.t('BULK_CONTAINER_PROCESSING_FAILED', {
                error: response.body.error,
              }),
              'error',
            );
            return reject(response.body.error);
          }
          return reject(err);
        }

        showToast(
          i18next.t('BULK_CONTAINER_AVAILABILITY'),
          i18next.t('BULK_CONTAINER_PROCESSED_SUCCESS'),
          'success',
        );
        fileDownload(response.text, 'container-availability.csv');

        resolve();
      });
    });
  } catch (e) {
    showToast(i18next.t('CONTAINER_AVAILABILITY_FAILED'), e.message, 'error');
    throw e;
  }
}

export async function generateStripePaymentLink({
  container_number,
  master_bill_number,
  window_start,
  window_end,
  yard,
  success_url,
  cancel_url,
}) {
  const data = {
    container_number,
    master_bill_number,
    window_start,
    window_end,
    yard,
    success_url,
    cancel_url,
  };
  try {
    const result = await client({
      method: 'POST',
      path: '/api/appointments/payment-summary',
      entity: JSON.stringify(data),
    });

    return result?.entity?.data?.paymentURL;
  } catch (e) {
    showToast(i18next.t('GENERATE_PAYMENT_LINK_FAILED'), e.message, 'error');
    throw e;
  }
}
